import { AxiosResponse } from 'axios';
import { EidProvider, Language } from '@/types';
import { client } from '@/client';
import { CodeRequest } from '@/auth';

// Form endpoints are for ADMIN_USER
// GDPR endpoints are for END_USER
// Message endpoints are for COMPANY_USER / COMPANY_ADMIN

export enum FormPropertyType {
  CHECKBOX = 'CHECKBOX',
  MULTI_CHECKBOX = 'MULTI_CHECKBOX',
  SELECT = 'SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  RADIO_BUTTON_GROUP = 'RADIO_BUTTON_GROUP',
  TEXT_AREA = 'TEXT_AREA',
  FILE = 'FILE',
  TEXT = 'TEXT',
  SIGNATURE_TEXT = 'SIGNATURE_TEXT',
}

export enum FormPropertyConstraintType {
  REQUIRED = 'REQUIRED',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  URL = 'URL',
  DATE = 'DATE',
  NUMBER = 'NUMBER',
  PATTERN = 'PATTERN',
  SSN = 'SSN',
  ORG_NUMBER = 'ORG_NUMBER',
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  MIN = 'MIN',
  MAX = 'MAX',
}

export type FormIdAuthRequest = {
  method:
    | 'BANKID_SE'
    | 'FREJA'
    | 'BANKID_NO'
    | 'MITID'
    | 'FTN'
    | 'VIPPS'
    | 'MOBILE_PAY';
};

export type FormIdAuthStatusResponse = {
  id: string;
} & (
  | {
      status: 'IN_PROGRESS';
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
      token: string;
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export type UpdateFormRequest = {
  autodeleteCountDays?: number | null;
  companyNotificationText: string;
  email: string;
  name: string;
  path: string;
  customNotes?: string;
  header: string;
  intent?: string;
  introText?: string;
  providers: EidProvider[];
  language: Language;
  logoUrl?: string;
  showFooter: boolean;
  properties: {
    header?: string;
    ingress?: string;
    description?: string;
    position: number;
    label: string;
    name: string;
    type: FormPropertyType;
    options: string[];
    constraints: {
      type: FormPropertyConstraintType;
      expression?: string;
    }[];
  }[];
  successIntroText?: string;
  successText?: string;
  textUnderButton?: string;
  referenceTextDenmark?: string;
  frejaMinRegistrationLevel?: 'EXTENDED' | 'PLUS';
  backgroundImage?: string;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  hosts: string[];
  premium: boolean;
  companySignatoryEnabled: boolean;
  orgNumberProperty?: string;
  redirectUrl?: string;
  bankIdClient: number | null;
  frejaClient: number | null;
  bankIdNoClient: number | null;
  vippsClient: number | null;
  mitIdClient: number | null;
  ftnClient: number | null;
};

export type Form = {
  id: number;
  active: boolean;
  companyId: number;
  name: string;
  path: string;
  customNotes?: string;
  email: string;
  header: string;
  autodeleteCountDays?: number | null;
  successText: string;
  introText: string;
  textUnderButton?: string;
  companyNotificationText: string;
  successIntroText: string;
  logoUrl: string;
  showFooter: boolean;
  providers: EidProvider[];
  language: Language;
  intent: string;
  deleted: boolean;
  properties: {
    id: number;
    position: number;
    label: string;
    name: string;
    type: FormPropertyType;
    formId: number;
    header?: string;
    ingress?: string;
    description?: string;
    options: string[];
    constraints: {
      type: FormPropertyConstraintType;
      expression?: string;
    }[];
  }[];
  referenceTextDenmark?: string;
  frejaMinRegistrationLevel?: 'EXTENDED' | 'PLUS';
  backgroundImage?: string;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  hosts: string[];
  fontName?: string;
  premium: boolean;
  emailConfirmation: boolean;
  emailProperty?: string;
  companySignatoryEnabled: boolean;
  orgNumberProperty?: string;
  redirectUrl?: string;
  bankIdClient: number | null;
  frejaClient: number | null;
  bankIdNoClient: number | null;
  vippsClient: number | null;
  mitIdClient: number | null;
  ftnClient: number | null;
};

export type GdprConfigurationProperty = {
  id: number;
  position: number;
  label: string;
  name: string;
  type: FormPropertyType;
  formId: number;
  header?: string;
  ingress?: string;
  description?: string;
  hash?: string;
  options: string[];
  constraints: {
    type: FormPropertyConstraintType;
    expression?: string;
  }[];
};

export type GdprConfiguration = {
  id: number;
  name: string;
  path: string;
  companyCode: string;
  successText?: string;
  heading: string;
  introText?: string;
  textUnderButton?: string;
  successIntroText?: string;
  companyLogoUrl?: string;
  showFooter: boolean;
  providers: EidProvider[];
  language: Language;
  properties: GdprConfigurationProperty[];
  referenceTextDenmark?: string;
  backgroundImage?: string;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  font?: string;
  emailConfirmation: boolean;
  emailProperty?: string;
  redirectUrl?: string;
};

export async function getGdprFormByOrigin(
  signal?: AbortSignal,
): Promise<GdprConfiguration> {
  return client
    .get<GdprConfiguration>('/formid', {
      signal,
    })
    .then((response) => response.data);
}

export async function getGdprFormByName(
  formPath: string,
  companyCode?: string,
  signal?: AbortSignal,
): Promise<GdprConfiguration> {
  return client
    .get<GdprConfiguration>(`/formid/${formPath}`, {
      params: {
        companyCode,
      },
      signal,
    })
    .then((response) => response.data);
}

export async function getFormByCode(
  companyCode: string,
  formPath: string,
  signal?: AbortSignal,
): Promise<Form> {
  return client
    .get<Form>(`/admin/form/company/${companyCode}/form/${formPath}`, {
      signal,
    })
    .then((response) => response.data);
}

export async function getFormsByCompany(
  signal?: AbortSignal,
): Promise<GdprConfiguration[]> {
  return client
    .get<GdprConfiguration[]>('/message/gdpr', { signal })
    .then((response) => response.data);
}

export async function updateForm(
  companyCode: string,
  data: UpdateFormRequest,
  signal?: AbortSignal,
): Promise<Form> {
  return client
    .post<UpdateFormRequest, AxiosResponse<Form>>(
      `/admin/form/company/${companyCode}`,
      data,
      { signal },
    )
    .then((response) => response.data);
}

export async function updateFormFont(
  formId: number,
  file: File,
  signal?: AbortSignal,
): Promise<void> {
  const data = new FormData();
  data.append('file', file);

  return client
    .put(`/admin/form/${formId}/font`, data, { signal })
    .then((response) => response.data);
}

export async function postForm(
  formPath: string,
  companyCode: string,
  form: object,
  token: string,
  signal?: AbortSignal,
): Promise<void> {
  const data = new FormData();

  for (const [key, value] of Object.entries(form)) {
    if (value !== undefined && value !== null) {
      if (value instanceof Blob) {
        data.append(key, value);
      } else if (value instanceof Array) {
        if (value[0] instanceof File) {
          const fileNames: string[] = [];

          for (const file of value) {
            data.append(file.name, file);
            fileNames.push(file.name);
          }

          data.append(key, JSON.stringify(fileNames));
        } else {
          data.append(key, JSON.stringify(value));
        }
      } else {
        data.append(key, String(value));
      }
    }
  }

  return client
    .post(`/formid/${formPath}`, data, {
      headers: {
        Authorization: `Basic ${window.btoa(`${token}:${token}`)}`,
      },
      params: {
        companyCode,
      },
      signal,
    })
    .then((response) => response.data);
}

export async function initFormIdAuth(
  formId: number,
  request: FormIdAuthRequest,
  signal?: AbortSignal,
): Promise<Extract<FormIdAuthStatusResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .post('/formid/auth', request, { params: { formId }, signal })
    .then((response) => response.data);
}

export async function getFormIdAuthStatus(
  id: string,
  formId: number,
  signal?: AbortSignal,
): Promise<FormIdAuthStatusResponse> {
  return client
    .get(`/formid/auth/${encodeURIComponent(id)}`, {
      params: { formId },
      signal,
    })
    .then((response) => response.data);
}

export async function completeFormIdAuth(
  id: string,
  formId: number,
  request: CodeRequest,
  signal?: AbortSignal,
): Promise<Extract<FormIdAuthStatusResponse, { status: 'COMPLETED' }>> {
  return client
    .post(`/formid/auth/${encodeURIComponent(id)}`, request, {
      params: { formId },
      signal,
    })
    .then((response) => response.data);
}
